import { HTMLAttributes } from 'react'
import { Box, Container, Grid, IconButton, SvgIcon, Typography } from '@mui/material'
import parse from 'html-react-parser'

import Button from '@/components/base/button'
import Media from '@/components/base/media'
import Spacer from '@/components/sections/spacer'
import { useEmblaCarouselHook } from '@/hooks'

import { WPLink, WPMedia } from '@/types'

export type MediaSliderProps = HTMLAttributes<HTMLDivElement> & {
  media: WPMedia
  content: {
    content: {
      pretitle: string
      title: string
      subtitle: string
      text: string
    }
    button: {
      link: WPLink
      icon: string
    }
  }[]
  is_first?: boolean
}

const MediaSlider = ({ media, content, is_first, ...props }: MediaSliderProps) => {
  const hasMultiple = content.length > 1
  const { emblaRef, scrollPrev, scrollNext, nextBtnDisabled, prevBtnDisabled } =
    useEmblaCarouselHook({
      loop: false,
      skipSnaps: false
    })

  const renderItem = (
    item: {
      content: {
        pretitle: string
        title: string
        subtitle: string
        text: string
      }
      button: {
        link: WPLink
        icon: string
      }
    },
    index: number
  ) => (
    <Box
      key={index}
      sx={() => ({
        flex: hasMultiple ? '0 0 100%' : '1',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: { xs: 2, sm: 3, md: 0 },
        paddingRight: { xs: 2, sm: 3, md: 0 },
        gap: 4
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        {item.content.pretitle ? (
          <Typography variant="overline" color="text.secondaryContrastText">
            {item.content.pretitle}
          </Typography>
        ) : null}
        <Spacer size="xxs" />
        {item.content.title ? (
          <Typography variant="h3" component={is_first ? 'h1' : 'h2'}>
            {item.content.title}xx
          </Typography>
        ) : null}
        <Spacer size="xs" />
        {item.content.subtitle ? (
          <Typography variant="textLarge" color="text.primary">
            {item.content.subtitle}
          </Typography>
        ) : null}
        <Spacer size="sm" />
        {item.content.text ? (
          <Typography variant="textRegular" color="text.secondary">
            {parse(item.content.text)}
          </Typography>
        ) : null}
        <Spacer size="md" />
        {item.button?.link?.url ? (
          <Button
            href={item.button.link.url}
            variant="contained"
            color="primary"
            icon={item.button.icon}
          >
            {item.button.link.title}
          </Button>
        ) : null}
      </Box>
    </Box>
  )

  return (
    <Box {...props}>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 6 }}>
            <Box
              sx={{
                width: '100%',
                aspectRatio: '672/762',
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                borderRadius: (theme) => theme.shape.borderRadius6
              }}
            >
              <Media {...media} />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 4, sm: 8, md: 6 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ position: 'relative' }}>
              {hasMultiple ? (
                <>
                  <Box
                    ref={emblaRef}
                    sx={{
                      overflow: 'hidden',
                      marginLeft: { xs: -2, sm: -3, md: 0 },
                      marginRight: { xs: -2, sm: -3, md: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>{content.map(renderItem)}</Box>
                  </Box>
                  <Spacer size="xs" />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="medium"
                      onClick={scrollPrev}
                      disabled={prevBtnDisabled}
                      color="secondary"
                    >
                      <SvgIcon fontSize="medium">
                        <use href="#ico-arrow-prev" />
                      </SvgIcon>
                    </IconButton>
                    <IconButton
                      size="medium"
                      onClick={scrollNext}
                      disabled={nextBtnDisabled}
                      color="secondary"
                    >
                      <SvgIcon fontSize="medium">
                        <use href="#ico-arrow-next" />
                      </SvgIcon>
                    </IconButton>
                  </Box>
                </>
              ) : (
                content.map(renderItem)
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default MediaSlider
